/* eslint-disable react-hooks/exhaustive-deps */
import { Drawer, Tabs } from 'antd';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from "react-router-dom";
import React, { useState, useEffect, useMemo } from 'react';
import { SearchFilters, Header, DrawerHeader } from 'datastake-daf/dist/components';

import {
  getSelectFiltersConfig,
  getFiltersConfig,
  getCheckboxConfig,
  mapSubjects,
  getFilterOptions,
  getDefaultSelectOptions,
} from './helper';
import LinkedSubjectCreate from './create';
import getColumns from './columns';
import Styles from '../../../../../components/Forms/locations/style';
import { actions } from '../../../../../store/reducers/linked-subjects';
import { displayMessage } from '../../../../../helpers/Messages';
import { filterParams, useFilters } from '../../../../../helpers/SubjectSearch';
import { renderBreadCrumbs } from '../../../../../helpers/breadCrumbs';
import { CREATE_DRAWER_WIDTH } from '../../../../../helpers/form';
import DafTable from '../../../../../components/Table/newTable';
import TablePagination from '../../../../../components/Table/pagination';

const renderTitile = (view) => {
  switch (view) {
    case 'initiatives':
      return 'Initiatives';
    case 'victims':
      return 'individualsSidebar';
    case 'pictures':
      return 'Pictures';
    case 'documents':
      return 'Documents';
    case 'locations':
      return 'Locations';
    case 'stakeholders':
      return 'Stakeholders';
    default:
      return 'Linked Subjects';
  }
}

const module = 'tazama'

function Base({
  view,
  data,
  loading,
  editVisible,
  linkedSubjectsClear,
  linkedSubjectsGet,
  linkedSubjectsFormReset,
  linkedSubjectsView,
  user,
  setEditVisible,
  activeTab,
}) {
  const goTo = useNavigate();
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [initFetchDone, setInitFetchDone] = useState(false);

  const filtersConfig = useMemo(() => getFiltersConfig(linkedSubjectsView), [linkedSubjectsView]);
  const selectFiltersConfig = useMemo(() => getSelectFiltersConfig(linkedSubjectsView), [linkedSubjectsView]);
  const checkboxConfig = useMemo(() => getCheckboxConfig(linkedSubjectsView), [linkedSubjectsView]);

  useEffect(() => () => {
    linkedSubjectsClear();
  }, []);

  const {
    newFiltersConfig,
    pagination,
    activeFilters,
    onSearch,
    defaultFilters,
    onTableChange,
    setPagination,
    onFiltersChange,
    setActiveFilters,
    totalPages,
    canGoNext,
    canGoPrev,
    goPrev,
    goNext,
  } = useFilters({
    selectFiltersConfig,
    view: linkedSubjectsView,
    module,
    filtersConfig,
  });

  const columns = useMemo(() => getColumns(
    t,
    module,
    linkedSubjectsView,
    'show',
    activeFilters,
    goTo,
    activeTab
  ), [t, module, activeFilters, activeTab]);

  const [subjects, setSubjects] = useState([]);
  const [selectOptions, setSelectOptions] = useState(getDefaultSelectOptions(linkedSubjectsView));

  useEffect(() => {
    linkedSubjectsGet(filterParams({
      activeFilters: activeFilters,
      activeTab,
      module,
    }), linkedSubjectsView);

    if (!initFetchDone) {
      setInitFetchDone(true);
    }
  }, [activeFilters, user.language]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (data.data) {
      setSubjects(mapSubjects(data.data, data.options, linkedSubjectsView).map((s, i) => ({
        ...s,
        dbId: `${s.dbId}-${i}`,
      })))
    }

    setSelectOptions((prev) => ({
      ...prev,
      ...getFilterOptions(data.options, linkedSubjectsView),
    }));

    if (typeof data.totalItems === 'number') {
      setPagination((prev) => ({ ...prev, total: data.totalItems }));
    }
  }, [data]);

  useEffect(() => {
    if (initFetchDone) {
      setActiveFilters({ ...activeFilters, page: 1, tab: activeTab });
      setPagination({ ...pagination, current: 1 });
    }
  }, [activeTab]);

  const canClearSearch = useMemo(() => {
    return !!activeFilters.search;
  }, [activeFilters]);

  return (
    <>
      <div className="column start table-content">
        <div className='mt-6 ml-6 mr-6'>
          <SearchFilters
            t={t}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            hasError={hasError}
            setHasError={setHasError}
            filtersConfig={newFiltersConfig}
            checkboxConfig={checkboxConfig}
            onSearch={onSearch}
            activeFilters={activeFilters}
            canClear={canClearSearch}
            clearFilters={() => setActiveFilters({})}
          />
        </div>
        <div className='daf-table-wrapper pagination-w-padding'>
          <DafTable
            columns={columns}
            data={subjects}
            hideOnLoading={false}
            doEmptyRows
            loading={loading}
            pagination={pagination}
            rowKey="dbId"
            selectOptions={selectOptions}
            sourcesKey="authorId"
            filtersConfig={selectFiltersConfig}
            onFilterChange={onFiltersChange}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            defaultFilters={defaultFilters}
            onChange={onTableChange}
          />
          <TablePagination
            t={t}
            page={pagination.current}
            totalPages={totalPages}
            goPrev={goPrev}
            goNext={goNext}
            canGoNext={canGoNext}
            canGoPrev={canGoPrev}
            totalItems={pagination.total}
            doTotalItems
            onChangePagination={(val) => {
              onTableChange({ ...pagination, current: 1, pageSize: val });
            }}
            perPage={pagination.pageSize}
            arrowIcons
          />
        </div>
      </div>
      <Drawer
        destroyOnClose={true}
        title={<DrawerHeader title={t("Create Initiative")} />}
        open={editVisible}
        maskClosable={false}
        onClose={() => {
          setEditVisible(false);
          linkedSubjectsFormReset({ module, view: linkedSubjectsView });
        }}
        width={CREATE_DRAWER_WIDTH}
        bodyStyle={{ padding: 0 }}
      >
        {editVisible && (
          <LinkedSubjectCreate
            namespace={linkedSubjectsView}
            module={module}
            view={linkedSubjectsView}
            edit={false}
            onSubmitted={(type, m, data) => {
              displayMessage(type, m)
              if (data.datastakeId) {
                goTo(`/app/${module}/edit/${linkedSubjectsView}/${data.datastakeId}`)
              }
            }}
            onCancel={() => {
              setEditVisible(false);
              linkedSubjectsFormReset({ module, view: linkedSubjectsView });
            }}
          />
        )}
      </Drawer>
    </>
  )
}

function LinkedSubjects({
  view,
  linkedSubjects: { data, loading },
  linkedSubjectsGet,
  linkedSubjectsFormReset,
  linkedSubjectsClear,
  linkedSubjectsView = 'initiatives',
  authentication: { user, sourceId }
}) {
  const goTo = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(params.get('tab') || 'ownData');
  const [editVisible, setEditVisible] = useState(params.has('create'));

  const canEdit = linkedSubjectsView === 'initiatives';

  const breadCrumbs = useMemo(() =>
    renderBreadCrumbs({ view: linkedSubjectsView, t, goTo, mod: module }), [view, t, goTo]);

  return (
    <Styles className="semibold form-input-output">
      <Header
        title={t(renderTitile(linkedSubjectsView))}
        breadcrumbs={breadCrumbs}
        actionButtons={canEdit ? [
          {
            type: 'primary',
            onClick: () => setEditVisible(true),
            label: t("New"),
          },
        ] : []}
      />
      <Tabs
        items={[
          { label: t('Own Data'), key: 'ownData' },
          { label: t('Partners'), key: 'partners' },
        ]}
        defaultActiveKey={activeTab}
        onChange={(val) => {
          if (loading) {
            return;
          }

          setActiveTab(val)
        }}
        className="view-page-tabs mt-3"
      />
      <Base
        editVisible={editVisible}
        view={view}
        linkedSubjectsClear={linkedSubjectsClear}
        setEditVisible={setEditVisible}
        data={data}
        loading={loading}
        linkedSubjectsGet={linkedSubjectsGet}
        linkedSubjectsFormReset={linkedSubjectsFormReset}
        linkedSubjectsView={linkedSubjectsView}
        user={user}
        sourceId={sourceId}
        activeTab={activeTab}
      />
    </Styles>
  )
};

const mstp = (state) => ({ linkedSubjects: state.linkedSubjects, authentication: state.authentication });

export default connect(mstp, actions)(LinkedSubjects);
